import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type Ambassador,
  type DeliveriesExport,
  type DeliveriesFilters,
  type DeliveriesPage,
  type ReassignParams,
  type RegisteredAddress,
  type RescheduleParams,
} from "../../domain/entities/all-deliveries";
import {
  ExportDeliveriesError,
  ListAddressesError,
  ListAmbassadorsError,
  ListDeliveriesError,
  ReassignDeliveryError,
  RescheduleDeliveryError,
} from "../../domain/errors/errors";
import { type AllDeliveriesRepository } from "../../domain/repos/all-deliveries-repo";
import {
  type AddressesResponse,
  type AmbassadorsResponse,
  type DeliveriesResponse,
} from "../../infrastructure/responses/all-deliveries-responses";

const PAGE_SIZE = 100;

/** The snake_case filter params shared by the list and the export, so the file
 *  is narrowed by exactly what narrowed the screen (DMS-57). `HttpApiClient`
 *  takes only a URL, so the querystring is assembled by hand. */
const filterParams = (filters: DeliveriesFilters): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("status_group", filters.statusGroup);
  if (filters.supervisorId) params.set("supervisor_id", filters.supervisorId);
  if (filters.q) params.set("q", filters.q);
  if (filters.ambassadorId) params.set("assigned_ambassador_id", filters.ambassadorId);
  if (filters.from) params.set("delivery_slot_from", filters.from);
  if (filters.to) params.set("delivery_slot_to", filters.to);
  return params;
};

/** Builds the query string for GET /v1/requests: the filters plus the KPI
 *  summary and this page's cursor. */
const buildQuery = (filters: DeliveriesFilters): string => {
  const params = filterParams(filters);
  params.set("with_summary", "true");
  params.set("page_size", String(PAGE_SIZE));
  if (filters.pageToken) params.set("page_token", filters.pageToken);
  return `?${params.toString()}`;
};

/** Builds the query string for GET /v1/requests/export: the filters alone. The
 *  export has no page to ask for and no use for the KPI counts. */
const buildExportQuery = (filters: DeliveriesFilters): string =>
  `?${filterParams(filters).toString()}`;

/** The row count the backend reported, or undefined when it reported none (or
 *  something that is not a number) — which is not the same as an empty export. */
const parseRowCount = (header?: string): number | undefined => {
  if (header === undefined) return undefined;
  const count = Number(header);
  return Number.isInteger(count) && count >= 0 ? count : undefined;
};

/** The name used when the backend's Content-Disposition is missing — only ever
 *  a fallback, so the backend stays the one deciding what the file is called. */
const fallbackExportFilename = (): string => {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `entregas-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
};

export class AllDeliveriesRepo implements AllDeliveriesRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listDeliveries(filters: DeliveriesFilters): Promise<DeliveriesPage> {
    try {
      const response = await this.apiClient.get<DeliveriesResponse>(
        `v1/requests${buildQuery(filters)}`,
      );
      return {
        items: response.data?.items ?? [],
        nextToken: response.data?.nextToken,
        summary: response.data?.summary,
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListDeliveriesError(message, apiError);
    }
  }

  async exportDeliveries(filters: DeliveriesFilters): Promise<DeliveriesExport> {
    try {
      const response = await this.apiClient.getBlob(
        `v1/requests/export${buildExportQuery(filters)}`,
      );
      return {
        blob: response.blob,
        filename: response.filename ?? fallbackExportFilename(),
        rowCount: parseRowCount(response.headers["x-export-row-count"]),
        truncated: response.headers["x-export-truncated"] === "true",
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ExportDeliveriesError(message, apiError);
    }
  }

  async listAmbassadors(): Promise<Ambassador[]> {
    try {
      const response = await this.apiClient.get<AmbassadorsResponse>("v1/users?role=ambassador");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorsError(message, apiError);
    }
  }

  async listAddresses(requestId: string): Promise<RegisteredAddress[]> {
    try {
      const response = await this.apiClient.get<AddressesResponse>(
        `v1/requests/${requestId}/addresses`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAddressesError(message, apiError);
    }
  }

  async reassign(params: ReassignParams): Promise<void> {
    try {
      // The request interceptor snake_cases the body (newAmbassadorId ->
      // new_ambassador_id); the reassigning supervisor is the session user.
      await this.apiClient.post(`v1/requests/${params.requestId}/reassignment`, {
        newAmbassadorId: params.newAmbassadorId,
        reason: params.reason,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReassignDeliveryError(message, apiError);
    }
  }

  async reschedule(params: RescheduleParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/reschedule`, {
        deliveryDate: params.deliveryDate,
        deliveryShift: params.deliveryShift,
        reason: params.reason,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RescheduleDeliveryError(message, apiError);
    }
  }
}
